"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.withdrawOfferSchema = exports.recordAnswerSchema = exports.candidateDeclineSchema = exports.decideOfferStepSchema = exports.offerVersionSchema = exports.offerTermsSchema = exports.allowanceSchema = exports.offerLetterPreviewSchema = exports.OFFER_DRAFT_STAMP = exports.updateOfferLetterSchema = exports.offerLetterSchema = exports.OFFER_WITHDRAWN_REASON_CODE = exports.NO_ANSWER_WITHDRAWAL_CODE = exports.CANDIDATE_DECLINE_REASONS = exports.OFFER_MERGE_FIELDS = exports.CLOSED_OFFER_STATUSES = exports.WITHDRAWABLE_OFFER_STATUSES = exports.SEEN_BY_CANDIDATE_STATUSES = exports.OUT_WITH_CANDIDATE_STATUSES = exports.EDITABLE_OFFER_STATUSES = exports.OFFER_STATUS_LABELS = exports.WITHDRAW_JUSTIFICATION_MIN = exports.OFFER_LETTER_BODY_MAX = exports.OFFER_NOTE_MAX = exports.OFFER_ALLOWANCES_MAX = exports.JOINING_DATE_MAX_DAYS_AHEAD = exports.OFFER_EXPIRY_WARNING_HOURS = exports.OFFER_LINK_HEADER = void 0;
exports.unknownMergeFields = unknownMergeFields;
exports.fillLetter = fillLetter;
exports.letterParagraphs = letterParagraphs;
exports.letterSegments = letterSegments;
exports.grossMonthlyMinor = grossMonthlyMinor;
exports.bandPosition = bandPosition;
exports.needsExtraApproval = needsExtraApproval;
exports.businessDate = businessDate;
exports.addDays = addDays;
exports.offerValidUntil = offerValidUntil;
exports.offerWarningAt = offerWarningAt;
exports.joiningDateProblem = joiningDateProblem;
exports.mayRecordNoShow = mayRecordNoShow;
const zod_1 = require("zod");
const money_1 = require("./money");
const reason_codes_1 = require("./reason-codes");
const schemas_1 = require("./schemas");
// S23 — offers and approvals (OFR-01–03, ADMIN-16, F13.01–F13.11 and F13.14's button).
// Sources: master plan S23 and its planning decisions (DECISIONS-LOG S23 Q1–Q7); E11; D7;
// D13; D16; §L12; §L15; §L18; §L20; §L22 N-48–N-55, N-58, N-80, N-81; §L23; §L25 Offer; §L26.
/** The candidate's offer link travels in this header, never in an access log (as ADR 0019). */
exports.OFFER_LINK_HEADER = 'x-offer-link';
/** §L22 N-51: the candidate and the recruiter hear this long before the offer lapses. */
exports.OFFER_EXPIRY_WARNING_HOURS = 48;
/** §L26 joining date: not in the past, at most this many days ahead. */
exports.JOINING_DATE_MAX_DAYS_AHEAD = 180;
exports.OFFER_ALLOWANCES_MAX = 10;
exports.OFFER_NOTE_MAX = 1000;
exports.OFFER_LETTER_BODY_MAX = 20_000;
/** F13.11: "written justification" — a sentence, not a word. */
exports.WITHDRAW_JUSTIFICATION_MIN = 10;
/**
 * D13/D14: Bangladesh time, which has kept UTC+6 all year since 2009. A fixed offset is
 * exact here and keeps this package free of a time-zone library.
 */
const BUSINESS_UTC_OFFSET_MS = 6 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
exports.OFFER_STATUS_LABELS = {
    draft: 'Draft',
    in_approval: 'Waiting for approval',
    approved: 'Approved — ready to send',
    rejected: 'Sent back by an approver',
    sent: 'Sent',
    viewed: 'Opened by the candidate',
    negotiating: 'Being renegotiated',
    accepted: 'Accepted',
    declined: 'Declined',
    expired: 'Expired',
    joined: 'Joined',
    accepted_no_show: "Accepted — didn't join",
    withdrawn_by_company: 'Withdrawn',
};
/** The builder is open in these: a draft, one an approver sent back, an approved one not yet sent (§L12), and version N+1. */
exports.EDITABLE_OFFER_STATUSES = ['draft', 'rejected', 'approved', 'negotiating'];
/** Out with the candidate: a decline, an expiry, an acceptance or a revision can follow. */
exports.OUT_WITH_CANDIDATE_STATUSES = ['sent', 'viewed'];
/** S23 Q6: withdrawing after the candidate has seen an offer leaves them "Not selected"; before, a finalist again. */
exports.SEEN_BY_CANDIDATE_STATUSES = ['sent', 'viewed', 'negotiating', 'accepted'];
/** S23 Q6: the HR Lead may withdraw an offer at any point before joining. */
exports.WITHDRAWABLE_OFFER_STATUSES = ['draft', 'in_approval', 'rejected', 'approved', 'sent', 'viewed', 'negotiating', 'accepted'];
/** No further change is possible. */
exports.CLOSED_OFFER_STATUSES = ['declined', 'expired', 'joined', 'accepted_no_show', 'withdrawn_by_company'];
// --- §L12 merge fields --------------------------------------------------------------------------
exports.OFFER_MERGE_FIELDS = [
    { key: 'candidateName', label: 'Candidate full name' },
    { key: 'candidateAddress', label: 'Candidate address' },
    { key: 'jobTitle', label: 'Job title' },
    { key: 'department', label: 'Department' },
    { key: 'venture', label: 'Venture' },
    { key: 'employmentType', label: 'Employment type' },
    { key: 'workLocation', label: 'Work location' },
    { key: 'grossMonthlySalary', label: 'Gross monthly salary' },
    { key: 'salaryBreakdown', label: 'Salary breakdown' },
    { key: 'joiningDate', label: 'Joining date' },
    { key: 'probationPeriod', label: 'Probation period' },
    { key: 'noticePeriod', label: 'Notice period' },
    { key: 'reportingManager', label: 'Reporting manager' },
    { key: 'offerValidUntil', label: 'Offer valid until' },
    { key: 'signatoryName', label: 'Signatory name' },
    { key: 'signatoryDesignation', label: 'Signatory designation' },
    { key: 'issueDate', label: 'Issue date' },
    { key: 'offerReference', label: 'Offer reference number' },
];
const MERGE_KEYS = new Set(exports.OFFER_MERGE_FIELDS.map((field) => field.key));
function isMergeField(name) {
    return MERGE_KEYS.has(name);
}
/** `{{name}}`, with optional spaces inside the braces — the same shape as the email templates. */
const MERGE_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9_]*)\s*\}\}/g;
/** The `{{…}}` a letter uses that are not §L12 fields — refused when the letter is saved. */
function unknownMergeFields(body) {
    const unknown = new Set();
    for (const match of body.matchAll(MERGE_PATTERN)) {
        const name = match[1];
        if (name !== undefined && !isMergeField(name))
            unknown.add(name);
    }
    return [...unknown];
}
/** The letter with every field filled. An empty value reads "—", never a blank (06-qol §11). */
function fillLetter(body, values) {
    return body.replace(MERGE_PATTERN, (whole, name) => {
        if (!isMergeField(name))
            return whole;
        const value = values[name];
        return value.trim() === '' ? '—' : value;
    });
}
/** Blank lines separate a letter's paragraphs. */
const PARAGRAPH_BREAK = /\n\s*\n/;
/** A letter's paragraphs: blank lines separate them, as the editor shows them. */
function letterParagraphs(text) {
    return text
        .split(PARAGRAPH_BREAK)
        .map((paragraph) => paragraph.trim())
        .filter((paragraph) => paragraph !== '');
}
/**
 * ADMIN-16's preview (Admin redesign 2026-11-02): the same paragraphs as `letterParagraphs(fillLetter(body, values))`
 * — character for character, so the screen shows what the PDF prints — cut into runs that say which field each value
 * came from, so the preview can mark them.
 */
function letterSegments(body, values) {
    const runs = [];
    let read = 0;
    let written = 0;
    const push = (text, field, isUnknownField) => {
        if (text === '')
            return;
        runs.push({ text, field, isUnknownField, start: written });
        written += text.length;
    };
    for (const match of body.matchAll(MERGE_PATTERN)) {
        const at = match.index;
        push(body.slice(read, at), null, false);
        const name = match[1] ?? '';
        if (isMergeField(name))
            push(values[name].trim() === '' ? '—' : values[name], name, false);
        else
            push(match[0], null, true);
        read = at + match[0].length;
    }
    push(body.slice(read), null, false);
    const filled = runs.map((run) => run.text).join('');
    return paragraphRanges(filled).map(([from, to]) => runs.flatMap((run) => {
        const start = Math.max(from, run.start);
        const end = Math.min(to, run.start + run.text.length);
        return start < end ? [{ text: run.text.slice(start - run.start, end - run.start), field: run.field, isUnknownField: run.isUnknownField }] : [];
    }));
}
/** Where each paragraph of `letterParagraphs` sits in the text, trimmed, empty ones dropped. */
function paragraphRanges(text) {
    const ranges = [];
    let start = 0;
    for (const match of text.matchAll(new RegExp(PARAGRAPH_BREAK, 'g'))) {
        ranges.push([start, match.index]);
        start = match.index + match[0].length;
    }
    ranges.push([start, text.length]);
    return ranges.flatMap(([from, to]) => {
        const part = text.slice(from, to);
        const start = from + part.length - part.trimStart().length;
        const end = to - (part.length - part.trimEnd().length);
        return start < end ? [[start, end]] : [];
    });
}
/** S23 Q7: the band compares the gross monthly total — basic plus every allowance. */
function grossMonthlyMinor(baseMinor, allowances) {
    return allowances.reduce((sum, allowance) => sum + allowance.amountMinor, baseMinor);
}
/** Where an amount sits against the job's band. A band with one end checks that end only. */
function bandPosition(amountMinor, minMinor, maxMinor) {
    if (minMinor === null && maxMinor === null)
        return 'no_band';
    if (minMinor !== null && amountMinor < minMinor)
        return 'below';
    if (maxMinor !== null && amountMinor > maxMinor)
        return 'above';
    return 'inside';
}
/** §L25, S12 Q3: outside the band, and the job's switch on, adds the chain's extra approver. */
function needsExtraApproval(position, isBandEnforced) {
    return isBandEnforced && (position === 'below' || position === 'above');
}
// --- Dates (D13, §L26) --------------------------------------------------------------------------
/** Today's calendar date in Bangladesh, `YYYY-MM-DD`. */
function businessDate(now) {
    return new Date(now.getTime() + BUSINESS_UTC_OFFSET_MS).toISOString().slice(0, 10);
}
/** The calendar date `days` after `date` (`YYYY-MM-DD`). */
function addDays(date, days) {
    return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY_MS).toISOString().slice(0, 10);
}
/**
 * When an offer sent at `sentAt` lapses: the end of its last valid day, Bangladesh time.
 * Sent on Sunday with 7 days to answer, it is open until midnight the following Sunday.
 */
function offerValidUntil(sentAt, validityDays) {
    const lastDay = addDays(businessDate(sentAt), validityDays);
    return new Date(Date.parse(`${lastDay}T00:00:00Z`) + DAY_MS - BUSINESS_UTC_OFFSET_MS - 1);
}
/** When N-51 goes: 48 hours before the offer lapses. */
function offerWarningAt(validUntil) {
    return new Date(validUntil.getTime() - exports.OFFER_EXPIRY_WARNING_HOURS * 60 * 60 * 1000);
}
/** §L26: "Joining date can't be in the past", and no further than 180 days ahead. */
function joiningDateProblem(joiningDate, today) {
    if (joiningDate < today)
        return "Joining date can't be in the past.";
    if (joiningDate > addDays(today, exports.JOINING_DATE_MAX_DAYS_AHEAD))
        return `Joining date can't be more than ${exports.JOINING_DATE_MAX_DAYS_AHEAD} days ahead.`;
    return null;
}
/** F13.14: "Didn't join" is offered once the joining date has passed. */
function mayRecordNoShow(joiningDate, today) {
    return joiningDate !== null && joiningDate < today;
}
// --- Decline reasons (§L20) ---------------------------------------------------------------------
/** What a candidate may pick on the offer page — every §L20 reason but "without giving a reason". */
exports.CANDIDATE_DECLINE_REASONS = reason_codes_1.WITHDRAWAL_REASONS.filter((reason) => reason.code !== 'WD-NORESPONSE').map((reason) => ({ code: reason.code, label: reason.label }));
/** S23: an expiry and a no-show close the application with this §L20 code; the offer's status keeps them apart. */
exports.NO_ANSWER_WITHDRAWAL_CODE = 'WD-NORESPONSE';
/** §L19 GEN-OFFER-WITHDRAWN (S23 Q6): set only by withdrawing an offer the candidate has seen. */
exports.OFFER_WITHDRAWN_REASON_CODE = 'GEN-OFFER-WITHDRAWN';
// --- ADMIN-16: offer letters (S23 Q1) -----------------------------------------------------------
exports.offerLetterSchema = zod_1.z
    .object({
    name: zod_1.z.string().trim().min(2, 'Name this letter.').max(120),
    /** Null: every venture may use it. */
    ventureId: schemas_1.uuidSchema.nullable().default(null),
    signatoryName: zod_1.z.string().trim().min(2, 'Who signs it?').max(120),
    signatoryDesignation: zod_1.z.string().trim().min(2, 'Their designation.').max(120),
    body: zod_1.z.string().trim().min(20, 'Write the letter.').max(exports.OFFER_LETTER_BODY_MAX, `Letters can be up to ${exports.OFFER_LETTER_BODY_MAX} characters.`),
    isActive: zod_1.z.boolean().default(true),
})
    .superRefine((input, ctx) => {
    const unknown = unknownMergeFields(input.body);
    if (unknown.length > 0)
        ctx.addIssue({ code: 'custom', path: ['body'], message: `Not a letter field: ${unknown.map((name) => `{{${name}}}`).join(', ')}. Use the field buttons.` });
});
exports.updateOfferLetterSchema = zod_1.z.object({ version: zod_1.z.number().int().nonnegative(), letter: exports.offerLetterSchema });
// --- ADMIN-16's A4 preview (Admin redesign 2026-11-02) ------------------------------------------
/** Stamped across every page of a letter made before approval — the PDF and ADMIN-16's preview alike. */
exports.OFFER_DRAFT_STAMP = 'DRAFT — NOT APPROVED';
/**
 * The letter as typed, not yet saved. Only what the page prints: the body and the signatory. Looser than the saved
 * shape — a letter half-written still previews — and the unknown-field check is the preview's to show, not to refuse.
 */
exports.offerLetterPreviewSchema = zod_1.z.object({
    letter: zod_1.z.object({
        ventureId: schemas_1.uuidSchema.nullable().default(null),
        signatoryName: zod_1.z.string().trim().max(120),
        signatoryDesignation: zod_1.z.string().trim().max(120),
        body: zod_1.z.string().max(exports.OFFER_LETTER_BODY_MAX, `Letters can be up to ${exports.OFFER_LETTER_BODY_MAX} characters.`),
    }),
    /** A real offer to fill it from. Left out: clearly labelled sample values. */
    offerId: schemas_1.uuidSchema.optional(),
});
// --- OFR-01: the builder ------------------------------------------------------------------------
exports.allowanceSchema = zod_1.z.object({
    label: zod_1.z.string().trim().min(1, 'Name it.').max(60),
    amountMinor: money_1.minorUnitsSchema,
});
/** Everything the recruiter fills. Amounts are monthly gross (D13). */
exports.offerTermsSchema = zod_1.z.object({
    version: zod_1.z.number().int().nonnegative(),
    baseSalaryMinor: money_1.minorUnitsSchema.nullable(),
    allowances: zod_1.z.array(exports.allowanceSchema).max(exports.OFFER_ALLOWANCES_MAX).default([]),
    joiningDate: zod_1.z.iso.date('Pick a date.').nullable(),
    validityDays: zod_1.z.number().int('Whole days only.').min(1, 'At least one day.').max(60),
    probationMonths: zod_1.z.number().int('Whole months only.').min(0).max(12),
    noticePeriod: zod_1.z.string().trim().max(80).nullable().default(null),
    workLocation: zod_1.z.string().trim().max(120).nullable().default(null),
    /** §L15: an internal applicant's current manager is told before the offer is sent. */
    isCurrentManagerTold: zod_1.z.boolean().default(false),
});
exports.offerVersionSchema = zod_1.z.object({ version: zod_1.z.number().int().nonnegative() });
/** F13.03: approve, or reject with the reason (mandatory). */
exports.decideOfferStepSchema = zod_1.z
    .object({
    version: zod_1.z.number().int().nonnegative(),
    decision: zod_1.z.enum(['approve', 'reject']),
    reason: zod_1.z.string().trim().max(exports.OFFER_NOTE_MAX).nullable().default(null),
})
    .superRefine((input, ctx) => {
    if (input.decision === 'reject' && !input.reason)
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'Say why, so the recruiter can fix it.' });
});
const declineFields = {
    reasonCode: zod_1.z.string().trim().min(1, 'Pick a reason before continuing.').max(40),
    note: zod_1.z.string().trim().max(exports.OFFER_NOTE_MAX).nullable().default(null),
};
/** The candidate's decline (F13.08) — a §L20 reason is mandatory. */
exports.candidateDeclineSchema = zod_1.z.object(declineFields);
/** F13.07/F13.08 by the recruiter: an answer the candidate gave by phone. */
exports.recordAnswerSchema = zod_1.z.discriminatedUnion('answer', [
    zod_1.z.object({ answer: zod_1.z.literal('accepted'), version: zod_1.z.number().int().nonnegative() }),
    zod_1.z.object({ answer: zod_1.z.literal('declined'), version: zod_1.z.number().int().nonnegative(), ...declineFields }),
]);
/** F13.11 — HR Lead only, with written justification (§L23). */
exports.withdrawOfferSchema = zod_1.z.object({
    version: zod_1.z.number().int().nonnegative(),
    justification: zod_1.z.string().trim().min(exports.WITHDRAW_JUSTIFICATION_MIN, 'Write a sentence on why — it is kept with the offer.').max(exports.OFFER_NOTE_MAX),
});
//# sourceMappingURL=offers.js.map